import { COREBASEURL } from '@constants/global';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Duplicate-application guard for Step 1 (Basic Information).
 *
 * The volunteer create flow must block a user from re-applying to the same election with an
 * email / phone / VIN that already has an application. This module owns the network call and a
 * small state machine shared by BOTH triggers on the Basic Information step:
 *   - a live, debounced check as the three fields fill (instant reassurance / early warning), and
 *   - an authoritative re-check on "Continue" that gates the advance to Step 2.
 *
 * It deliberately uses a raw `fetch` (not the shared Redux `httpPostMethod`) because we need the
 * real HTTP status — `response.ok` — to honor the "advance only on a 2xx response" rule, plus the
 * full server error text for the dialog. `httpPostMethod` discards the status and passes only a
 * `title` string, and widening it would regress every other POST caller.
 */

export type DuplicateCheckStatus = 'idle' | 'checking' | 'passed' | 'duplicate' | 'error';

export interface DuplicateCheckValues {
	email: string;
	phoneNumber: string;
	votersCardRegistrationNumber: string;
}

export type DuplicateCheckResult =
	| { status: 'passed' }
	| { status: 'duplicate'; message: string }
	| { status: 'error'; message: string };

const LIVE_DEBOUNCE_MS = 600;

const DUPLICATE_FALLBACK = 'An application already exists for this election with these details.';
const ERROR_FALLBACK = 'We couldn’t verify your details right now. Please check your connection and try again.';

const normalisePhone = (raw: string) => {
	const trimmed = (raw ?? '').trim();
	return trimmed.startsWith('+') ? trimmed.slice(1) : trimmed;
};

const normaliseEmail = (raw: string) => (raw ?? '').trim();

const normaliseVin = (raw: string) => (raw ?? '').trim().toUpperCase();

/** Stable identity for a given triple + election, used for caching and stale-response guards. */
const buildKey = (values: DuplicateCheckValues, electionId: number | undefined) =>
	`${electionId ?? ''}|${normaliseEmail(values.email).toLowerCase()}|${normalisePhone(values.phoneNumber)}|${normaliseVin(
		values.votersCardRegistrationNumber
	)}`;

/**
 * Best-effort extraction of a human message from a non-2xx body. The api.core duplicate branch
 * returns `Conflict("…")` — a **bare JSON string**, not a ProblemDetails object — so we handle a
 * raw string, a `{ detail | title | message }` object (other error shapes), and plain text.
 */
const readErrorMessage = async (response: Response): Promise<string> => {
	try {
		const raw = await response.text();
		if (!raw) return '';
		let parsed: unknown;
		try {
			parsed = JSON.parse(raw);
		} catch {
			return raw.trim(); // plain-text body
		}
		if (typeof parsed === 'string') return parsed.trim();
		if (parsed && typeof parsed === 'object') {
			const o = parsed as Record<string, unknown>;
			return (
				(typeof o.detail === 'string' && o.detail) ||
				(typeof o.title === 'string' && o.title) ||
				(typeof o.message === 'string' && o.message) ||
				''
			);
		}
		return '';
	} catch {
		return '';
	}
};

/**
 * POST the duplicate check. Resolves to a discriminated result; only throws on an aborted request
 * (so the caller can ignore a superseded call). A duplicate is the backend's **409 Conflict**; any
 * other non-2xx (rate limit, 5xx, gateway) or a network failure resolves to `error` so an outage is
 * never surfaced to the user as "you've already applied".
 */
export const checkVolunteerApplicationExists = async (
	values: DuplicateCheckValues,
	electionId: number,
	signal?: AbortSignal
): Promise<DuplicateCheckResult> => {
	const body = {
		email: normaliseEmail(values.email),
		phoneNumber: normalisePhone(values.phoneNumber),
		electionId,
		votersCardRegistrationNumber: normaliseVin(values.votersCardRegistrationNumber),
	};

	try {
		const response = await fetch(`${COREBASEURL}Volunteer/application-exists`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
			signal,
		});

		if (response.ok) return { status: 'passed' };

		const serverMessage = await readErrorMessage(response);

		// 409 Conflict is api.core's "already applied" signal.
		if (response.status === 409) {
			return { status: 'duplicate', message: serverMessage || DUPLICATE_FALLBACK };
		}

		// Any other non-2xx — block advancing, but don't mislabel it as a duplicate.
		return { status: 'error', message: serverMessage || ERROR_FALLBACK };
	} catch (err) {
		if ((err as { name?: string })?.name === 'AbortError') throw err;
		return { status: 'error', message: ERROR_FALLBACK };
	}
};

export interface UseDuplicateCheckReturn {
	status: DuplicateCheckStatus;
	message: string;
	/** The triple that produced the current settled status (null while idle / after reset). */
	checkedKey: string | null;
	/** Authoritative check, awaited on Continue. Serves a cached result if the triple is unchanged. */
	runCheck: (values: DuplicateCheckValues) => Promise<DuplicateCheckResult>;
	/** Debounced live check while typing; no-ops on an already-known triple. */
	scheduleLiveCheck: (values: DuplicateCheckValues) => void;
	/** Clear all state (call when the three fields are no longer individually valid). */
	reset: () => void;
}

export const useDuplicateCheck = ({ electionId }: { electionId: number | undefined }): UseDuplicateCheckReturn => {
	const [status, setStatus] = useState<DuplicateCheckStatus>('idle');
	const [message, setMessage] = useState('');
	const [checkedKey, setCheckedKey] = useState<string | null>(null);

	// Last non-error result, keyed by triple — the cache that lets Continue resolve without a refetch.
	const lastRef = useRef<{ key: string; result: DuplicateCheckResult } | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const applyResult = useCallback((key: string, result: DuplicateCheckResult) => {
		lastRef.current = result.status === 'error' ? null : { key, result };
		setCheckedKey(result.status === 'error' ? null : key);
		setStatus(result.status);
		setMessage(result.status === 'passed' ? '' : result.message);
	}, []);

	const runKey = useCallback(
		async (values: DuplicateCheckValues, key: string): Promise<DuplicateCheckResult> => {
			if (!electionId) return { status: 'error', message: ERROR_FALLBACK };
			if (lastRef.current?.key === key) return lastRef.current.result;

			if (abortRef.current) abortRef.current.abort();
			const controller = new AbortController();
			abortRef.current = controller;
			setStatus('checking');

			try {
				const result = await checkVolunteerApplicationExists(values, electionId, controller.signal);
				if (abortRef.current === controller) applyResult(key, result);
				return result;
			} catch (err) {
				// Superseded by a newer request — let the newer one own the state.
				if ((err as { name?: string })?.name === 'AbortError') return { status: 'error', message: ERROR_FALLBACK };
				if (abortRef.current === controller) applyResult(key, { status: 'error', message: ERROR_FALLBACK });
				return { status: 'error', message: ERROR_FALLBACK };
			}
		},
		[electionId, applyResult]
	);

	const runCheck = useCallback(
		(values: DuplicateCheckValues) => runKey(values, buildKey(values, electionId)),
		[runKey, electionId]
	);

	const scheduleLiveCheck = useCallback(
		(values: DuplicateCheckValues) => {
			const key = buildKey(values, electionId);
			if (debounceRef.current) clearTimeout(debounceRef.current);

			// Already know this exact triple — reflect it immediately, no network.
			if (lastRef.current?.key === key) {
				applyResult(key, lastRef.current.result);
				return;
			}

			// New/unknown triple — clear any stale settled state so the chip can't show a stale pass.
			if (abortRef.current) abortRef.current.abort();
			setStatus('idle');
			setMessage('');
			setCheckedKey(null);
			debounceRef.current = setTimeout(() => {
				void runKey(values, key);
			}, LIVE_DEBOUNCE_MS);
		},
		[runKey, electionId, applyResult]
	);

	const reset = useCallback(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		if (abortRef.current) abortRef.current.abort();
		lastRef.current = null;
		setStatus('idle');
		setMessage('');
		setCheckedKey(null);
	}, []);

	useEffect(
		() => () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
			if (abortRef.current) abortRef.current.abort();
		},
		[]
	);

	return { status, message, checkedKey, runCheck, scheduleLiveCheck, reset };
};
